import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, MapPin, Truck, FileText, MessageCircle, ArrowRight } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import cities from '../content/cities.json';
import { KBEAUTY_IMAGES } from '../content/kbeautyContent';
import { whatsappUrl } from '../content/priceListContent';

const SITE = 'https://www.enhancements.eu';
const fadeInUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } };
const reveal = { initial: 'hidden', whileInView: 'visible', viewport: { once: true }, variants: { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } } };
const BRANDS = Object.keys(KBEAUTY_IMAGES);

export const cityMeta = (c) => ({
  title: `Distributore K-Beauty ${c.name} | Cosmetici Coreani e Rinforzanti Unghie per Farmacie`,
  description: `Distributore B2B a ${c.name} e in ${c.region} di cosmetici coreani K-Beauty (Anua, COSRX, Beauty of Joseon, Skin1004) e rinforzanti unghie Trind per farmacie, parafarmacie e profumerie. Consegna in ${c.delivery}.`
});

const buildJsonLd = (c) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: `Distributore K-Beauty ${c.name}`,
      serviceType: 'Distribuzione B2B all\'ingrosso di cosmetici coreani e rinforzanti unghie',
      provider: { '@type': 'WholesaleStore', name: 'Enhancements srl', url: SITE, telephone: '+39 350 133 9513' },
      areaServed: [{ '@type': 'City', name: c.name }, { '@type': 'AdministrativeArea', name: c.region }],
      brand: [...BRANDS, 'Trind'].map((name) => ({ '@type': 'Brand', name }))
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Distributore K-Beauty Italia', item: `${SITE}/distributore-k-beauty` },
        { '@type': 'ListItem', position: 3, name: c.name, item: `${SITE}/distributore-k-beauty/${c.slug}` }
      ]
    }
  ]
});

const Hero = ({ c }) => (
  <section className="section-spacing bg-[#F9F9F9]">
    <motion.div className="container-luxury" initial="hidden" animate="visible" variants={fadeInUp}>
      <div className="max-w-4xl">
        <p className="label-style text-gold mb-4 flex items-center gap-2"><MapPin size={14} /> {c.name} · {c.region}</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-6" data-testid="city-h1">Distributore K-Beauty a {c.name}: Cosmetici Coreani e Rinforzanti Unghie per Farmacie e Profumerie</h1>
        <p className="text-lg text-gray-600 leading-relaxed mb-8">Enhancements srl è il distributore B2B di cosmetici coreani e rinforzanti unghie Trind per farmacie, parafarmacie, profumerie, centri estetici ed e-commerce di {c.name} e provincia. Un unico fornitore per i brand K-Beauty più richiesti, con consegna in {c.delivery}.</p>
        <div className="flex flex-wrap gap-4">
          <Link to="/richiesta-listino" className="btn-primary inline-flex items-center gap-2" data-testid="city-pricelist-cta"><FileText size={16} /> Richiedi il listino B2B</Link>
          <a href={whatsappUrl(`Buongiorno, ho un'attività a ${c.name} e vorrei informazioni sui prezzi B2B.`)} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2" data-testid="city-whatsapp-cta"><MessageCircle size={16} /> Scrivici su WhatsApp</a>
        </div>
      </div>
    </motion.div>
  </section>
);

const Local = ({ c }) => (
  <section className="section-spacing">
    <motion.div className="container-luxury grid grid-cols-1 lg:grid-cols-2 gap-16" {...reveal}>
      <motion.div variants={fadeInUp}>
        <h2 className="font-heading text-3xl sm:text-4xl mb-6">Cosmetici coreani all'ingrosso a {c.name}</h2>
        <p className="text-gray-600 leading-relaxed mb-4">{c.local}</p>
        <p className="text-gray-600 leading-relaxed">Oltre alla skincare coreana distribuiamo Trind, il <Link to="/rinforzante-unghie-trind" className="text-gold underline">rinforzante unghie professionale</Link> per unghie fragili, uno dei prodotti più richiesti al banco della farmacia.</p>
      </motion.div>
      <motion.div variants={fadeInUp} className="bg-[#0A0A0A] text-white p-10">
        <h3 className="font-heading text-2xl mb-6 flex items-center gap-3"><Truck size={22} className="text-gold" /> Consegne in {c.region}</h3>
        <p className="text-gray-400 text-sm mb-4">Province servite:</p>
        <p className="text-gray-200 mb-6">{c.provinces.join(' · ')}</p>
        <p className="text-gray-400 text-sm mb-4">Zone di {c.name}:</p>
        <p className="text-gray-200">{c.areas.join(' · ')}</p>
      </motion.div>
    </motion.div>
  </section>
);

const Brands = ({ c }) => (
  <section className="section-spacing bg-[#F9F9F9]">
    <motion.div className="container-luxury" {...reveal}>
      <motion.h2 className="font-heading text-3xl sm:text-4xl mb-10" variants={fadeInUp}>Brand disponibili per i rivenditori di {c.name}</motion.h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {BRANDS.map((b) => (
          <motion.div key={b} variants={fadeInUp} className="bg-white">
            <img src={`/images/kbeauty/${KBEAUTY_IMAGES[b]}`} alt={`${b} distributore ${c.name}`} className="aspect-square w-full object-cover" loading="lazy" />
            <p className="p-4 font-heading text-lg">{b}</p>
          </motion.div>
        ))}
        <motion.div variants={fadeInUp} className="bg-white">
          <Link to="/rinforzante-unghie-trind" className="block" data-testid="city-trind-link">
            <img src="https://customer-assets.emergentagent.com/job_luxury-cosmetics-hub/artifacts/pimj9vxn_WVW_3620-bewerkt.jpg" alt={`Rinforzante unghie Trind ${c.name}`} className="aspect-square w-full object-cover" loading="lazy" />
            <p className="p-4 font-heading text-lg">Trind</p>
          </Link>
        </motion.div>
      </div>
    </motion.div>
  </section>
);

const Why = ({ c }) => (
  <section className="section-spacing">
    <motion.div className="container-luxury max-w-none" {...reveal}>
      <motion.h2 className="font-heading text-3xl sm:text-4xl mb-8" variants={fadeInUp}>Perché scegliere Enhancements a {c.name}</motion.h2>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
        {[`Consegna a ${c.name} in ${c.delivery}`, 'Unico fornitore per più brand coreani', 'Prodotti originali con etichettatura conforme UE', 'Listini B2B per farmacie e profumerie', 'Assistenza commerciale in italiano', 'Vendita riservata a Partita IVA'].map((a) => (
          <motion.li key={a} variants={fadeInUp} className="flex items-center gap-3"><Check size={18} className="text-gold flex-shrink-0" /><span className="text-gray-700">{a}</span></motion.li>
        ))}
      </ul>
      <motion.div variants={fadeInUp}>
        <h3 className="font-heading text-xl mb-4">Serviamo anche</h3>
        <div className="flex flex-wrap gap-3">
          {cities.filter((x) => x.slug !== c.slug).map((x) => (
            <Link key={x.slug} to={`/distributore-k-beauty/${x.slug}`} className="px-4 py-2 border border-gray-300 hover:border-[#C5A059] text-sm transition-colors" data-testid={`city-link-${x.slug}`}>Distributore K-Beauty {x.name}</Link>
          ))}
          <Link to="/distributore-k-beauty" className="px-4 py-2 bg-[#0A0A0A] text-white text-sm inline-flex items-center gap-2">Tutta Italia <ArrowRight size={14} /></Link>
        </div>
      </motion.div>
    </motion.div>
  </section>
);

const City = ({ c }) => {
  const jsonLd = useMemo(() => buildJsonLd(c), [c]);
  useSeo(cityMeta(c), `/distributore-k-beauty/${c.slug}`, jsonLd);
  return (
    <main className="pt-20" data-testid={`city-page-${c.slug}`}>
      <Hero c={c} />
      <Local c={c} />
      <Brands c={c} />
      <Why c={c} />
    </main>
  );
};

export default function CityPage() {
  const { city } = useParams();
  const c = cities.find((x) => x.slug === city);
  if (!c) return <Navigate to="/distributore-k-beauty" replace />;
  return <City key={c.slug} c={c} />;
}
